'use client';

import { getToolById } from '@/lib/tools/tool-registry';
import { persistToolEventNowOrQueue } from '@/lib/offline-sync-queue';
import { dispatchToolSharePrompt } from '@/lib/tool-share-prompt';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

type EventDetails = {
  inputLabel?: string;
  outputFormat?: string;
  fileSize?: string;
  [key: string]: unknown;
};

function getGuestSessionId() {
  if (typeof window === 'undefined') {
    return null;
  }

  const storageKey = '2xora_guest_session_id';
  const existing = window.localStorage.getItem(storageKey);

  if (existing) {
    return existing;
  }

  const created =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `guest-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  window.localStorage.setItem(storageKey, created);
  return created;
}

function shouldSkipOpenEvent(toolId: string) {
  if (typeof window === 'undefined') {
    return false;
  }

  const storageKey = `2xora_tool_open_${toolId}_${window.location.pathname}`;
  const existing = window.sessionStorage.getItem(storageKey);
  const now = Date.now();

  if (existing && now - Number(existing) < 3000) {
    return true;
  }

  window.sessionStorage.setItem(storageKey, String(now));
  return false;
}

function queueEventPersist(toolId: string, action: string, details: EventDetails = {}, toolName?: string) {
  if (typeof window === 'undefined') {
    return;
  }

  const tool = getToolById(toolId);
  const resolvedToolName = toolName ?? tool?.name ?? toolId;
  const toolCategory = tool?.category ?? 'Unknown';

  window.setTimeout(() => {
    void persistToolEventNowOrQueue({
        guestSessionId: getGuestSessionId(),
        toolId,
        toolName: resolvedToolName,
        toolCategory,
        action,
        inputLabel: typeof details.inputLabel === 'string' ? details.inputLabel : undefined,
        outputFormat: typeof details.outputFormat === 'string' ? details.outputFormat : undefined,
        fileSize: typeof details.fileSize === 'string' ? details.fileSize : undefined,
      });
  }, 0);
}

function shouldPromptShareOnAction(action: string) {
  const normalized = action.toLowerCase();

  if (['open', 'copy', 'copied', 'share_prompt_copy', 'share_prompt_share'].includes(normalized)) {
    return false;
  }

  return [
    'generate',
    'process',
    'convert',
    'compress',
    'lock',
    'auto_remove',
    'apply_signature',
    'export_pdf',
    'export_word_draft',
    'convert_word_back',
    'generate_link',
  ].some((keyword) => normalized.includes(keyword));
}

export function trackEvent(eventName: string, eventParams: Record<string, unknown> = {}) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

export function trackToolOpen(toolId: string, toolName: string) {
  if (shouldSkipOpenEvent(toolId)) {
    return;
  }

  trackEvent('tool_open', {
    tool_id: toolId,
    tool_name: toolName,
    event_category: 'tool_usage',
  });
  queueEventPersist(toolId, 'open', {}, toolName);
}

export function trackToolAction(toolId: string, action: string, details?: EventDetails) {
  const tool = getToolById(toolId);

  trackEvent('tool_action', {
    tool_id: toolId,
    action,
    event_category: 'tool_interaction',
    ...details,
  });
  queueEventPersist(toolId, action, details ?? {});

  if (shouldPromptShareOnAction(action)) {
    dispatchToolSharePrompt({
      toolId,
      toolName: tool?.name ?? toolId,
      format: typeof details?.outputFormat === 'string' ? details.outputFormat : 'Live tool',
    });
  }
}

export function trackDownload(toolId: string, format: string, details?: EventDetails) {
  const tool = getToolById(toolId);

  trackEvent('file_download', {
    tool_id: toolId,
    file_format: format,
    event_category: 'downloads',
  });
  queueEventPersist(toolId, 'download', { ...(details ?? {}), outputFormat: format });
  dispatchToolSharePrompt({
    toolId,
    toolName: tool?.name ?? toolId,
    format,
  });
}

export function trackToolSuccess(toolId: string, successType: string, details?: EventDetails) {
  const tool = getToolById(toolId);
  const format = typeof details?.outputFormat === 'string' ? details.outputFormat : 'Live tool';

  trackEvent('tool_success', {
    tool_id: toolId,
    success_type: successType,
    event_category: 'tool_success',
    ...details,
  });
  queueEventPersist(toolId, successType, details ?? {});
  dispatchToolSharePrompt({
    toolId,
    toolName: tool?.name ?? toolId,
    format,
  });
}

export function trackConversion(step: string, toolId?: string) {
  trackEvent('conversion_step', {
    step,
    tool_id: toolId,
    event_category: 'funnel',
  });
}
